// src/lib/index-metrics.ts
// What happened to a package's content during indexing, stage by stage —
// stored per package version (api.packages.index_metrics) so admins can see
// where content was dropped and how much of the index the LLM actually wrote.
//
// The failure counters are the early-warning signal: a dead or rate-limited
// model doesn't fail indexing (every LLM step falls back to a deterministic
// placeholder), it silently turns summaries into templates. A rising
// `failed` share across recent indexes is that outage, visible.

/** Why a file was dropped outright by the indexer's deterministic rules. */
export type SkipReason = 'metadata' | 'build-output' | 'empty' | 'minified';

export const INDEX_METRICS_VERSION = 1;

export interface IndexMetrics {
  version: typeof INDEX_METRICS_VERSION;
  /** Index model the summaries were requested from. */
  model: string;
  files: {
    /** Readable files collected from the package/repo. */
    collected: number;
    /** Skipped at collection for exceeding MAX_SOURCE_FILE_MB (set by the pipeline). */
    oversized?: number;
    /** Dropped by deterministic rules, by reason. */
    filteredOut: Partial<Record<SkipReason, number>>;
    /** Dropped by directory-level LLM triage (large repos only). */
    dirTriageSkipped: number;
    /** Ambiguous files sent to per-file LLM triage, and how many it excluded. */
    llmTriaged: number;
    llmTriageExcluded: number;
    /** Files that made it into the index. */
    indexed: number;
    /** …of which parsed into symbols by the AST parser. */
    astParsed: number;
    /** …of which the parser failed on (indexed as whole files instead). */
    astFailed?: number;
    /** …of which too small to summarize. */
    trivial: number;
  };
  summaries: {
    files: SummaryCounts & { cached: number };
    symbols: SummaryCounts;
    directories: SummaryCounts;
  };
  llm: { calls: number; promptTokens: number; completionTokens: number; cost: number };
  /** Docs crawled into the package (absent when no docs site was crawled). */
  docs?: DocsCrawlMetrics;
  /** RDF triple extraction (code-rdfs) — absent when it is turned off. */
  codeGraph?: CodeGraphMetrics;
  /** Filled after embedding: what retrieval actually searches. */
  retrieval?: {
    nodes: number;
    /** Nodes below PRUNE_MIN_IMPORTANCE, never embedded. */
    prunedNodes: number;
    vectors: number;
    edges: Record<string, number>;
  };
}

/**
 * What happened to a package's triple extraction. Recorded so the next index
 * of the package can skip an extraction that cannot fit (see code-graph.ts
 * shouldSkipCodeGraph) instead of rediscovering it at the machine's expense.
 */
export interface CodeGraphMetrics {
  status:
    | 'stored'
    /** Extracted, but storing the graph failed. */
    | 'store-failed'
    /** No triples (or no file in a language the extractor supports). */
    | 'empty'
    /** Finished, but the graph was over CODE_GRAPH_MAX_MB and was discarded. */
    | 'over-cap'
    /** Killed before the machine ran out of memory. */
    | 'out-of-memory'
    /** Errored or timed out. */
    | 'failed'
    /** Not attempted: a recent attempt on as many files did not fit. */
    | 'skipped'
    /** The extractor is not installed. */
    | 'unavailable';
  /** Files handed to the extractor (those in a language it supports). */
  files: number;
  triples?: number;
  /** Size of the graph it wrote, uncompressed. */
  outputMb?: number;
  /** Highest resident memory seen for the extractor process. */
  peakRssMb?: number;
  durationMs?: number;
  /** For 'skipped': the attempt that predicted this one would not fit. */
  basedOn?: { status: 'over-cap' | 'out-of-memory'; files: number; at: string };
  /** When this outcome was decided (ISO). */
  at: string;
}

export interface SummaryCounts {
  /** Written by the LLM. */
  llm: number;
  /** Deterministic by design (below the LLM threshold, tiny, tier 2). */
  template: number;
  /** The LLM was asked but failed; a placeholder was stored instead. */
  failed: number;
}

export interface DocsCrawlMetrics {
  /** The docs URL that was crawled. */
  url: string;
  /** Where the page list came from. */
  source: 'llms.txt' | 'crawl';
  /** Entries in the llms.txt that was used (0 for a link crawl). */
  listed: number;
  /** …on the docs host and under the docs path. */
  inScope: number;
  /** Pages skipped as being about other languages only (by URL or title). */
  otherLanguagePages: number;
  /** Code blocks removed as clearly in another language. */
  otherLanguageCodeBlocks: number;
  pagesWritten: number;
}

/** Share of LLM-attempted summaries that failed, 0–1 (null when nothing was attempted). */
export function summaryFailureRate(m: Pick<IndexMetrics, 'summaries'>): number | null {
  const s = m.summaries;
  const failed = s.files.failed + s.symbols.failed + s.directories.failed;
  const attempted = failed + s.files.llm + s.symbols.llm + s.directories.llm;
  return attempted > 0 ? failed / attempted : null;
}

/**
 * A summary failure rate above this marks an index as degraded: the LLM was
 * asked and fell back to placeholders (an outage, a dead model, rate limits).
 */
export const FAILURE_RATE_ALERT = 0.1;

/** Everything removed before summarization: rules, size cap, directory and file triage. */
export function filteredTotal(m: IndexMetrics): number {
  const byRule = Object.values(m.files.filteredOut).reduce((a, b) => a + (b ?? 0), 0);
  return byRule + (m.files.oversized ?? 0) + m.files.dirTriageSkipped + m.files.llmTriageExcluded;
}

/**
 * Share of file and directory summaries the LLM wrote (fresh or from cache),
 * 0–1 — the headline "did the model do its job" figure.
 *
 * Symbol summaries are left out on purpose: most are deterministic by design
 * (only high-importance symbols in tier-1 files go to the LLM), and a large
 * package has tens of thousands of them, so counting them buries the signal —
 * sympy read 6% with 1,461 of its 1,476 files summarized by the LLM. Their
 * counts are still in the per-index breakdown, and their failures still count
 * toward summaryFailureRate.
 */
export function fileLlmShare(m: Pick<IndexMetrics, 'summaries'>): number | null {
  const { files, directories } = m.summaries;
  const llm = files.llm + files.cached + directories.llm;
  const total = llm + files.template + files.failed + directories.template + directories.failed;
  return total > 0 ? llm / total : null;
}

/** Headline figures across a set of indexes — the admin panel's stat cards. */
export interface IndexMetricsSummary {
  /** Indexes whose summaryFailureRate is above FAILURE_RATE_ALERT. */
  flagged: number;
  /** Mean fileLlmShare over the indexes that have one (null when none do). */
  avgFileLlmShare: number | null;
  /** Total LLM spend, USD. */
  cost: number;
}

/**
 * Summarize a set of indexes. Takes only the parts it reads so the server can
 * summarize every matching index without loading their full metrics.
 */
export function summarizeIndexMetrics(
  list: ReadonlyArray<Pick<IndexMetrics, 'summaries' | 'llm'>>
): IndexMetricsSummary {
  let flagged = 0;
  let shareSum = 0;
  let shareCount = 0;
  let cost = 0;
  for (const m of list) {
    const rate = summaryFailureRate(m);
    if (rate !== null && rate > FAILURE_RATE_ALERT) flagged++;
    const share = fileLlmShare(m);
    if (share !== null) {
      shareSum += share;
      shareCount++;
    }
    cost += m.llm.cost;
  }
  return { flagged, avgFileLlmShare: shareCount ? shareSum / shareCount : null, cost };
}
